import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ArrowLeft, CheckCircle, Search, Package, Calendar,
  ChevronRight, Truck, User, Briefcase, Building2,
} from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import useClienteActivo from '@/hooks/useClienteActivo';
import ClienteActivoSelector from '@/components/ClienteActivoSelector';
import { fetchEntregasClientes } from '@/services/entregasClientes.service';

const ITEMS_PER_PAGE = 20;

const ClientePrendasEntregas = () => {
  const navigate = useNavigate();
  const {
    clientesAsignados,
    clienteActivoId,
    setClienteActivo,
  } = useClienteActivo();

  const [entregas, setEntregas] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [selected, setSelected] = useState(null);

  const sinClientes = clientesAsignados.length === 0;

  const loadData = useCallback(async () => {
    if (!clienteActivoId) return;
    setIsLoading(true);
    try {
      const response = await fetchEntregasClientes({
        query: { 'cliente.id': clienteActivoId, tipo: 'Entrega' },
        limit: ITEMS_PER_PAGE,
        page,
      });
      setEntregas(response.data || []);
      setTotalRecords(response.totalRecords || 0);
    } catch {
      setEntregas([]);
    } finally {
      setIsLoading(false);
    }
  }, [clienteActivoId, page]);

  useEffect(() => { loadData(); }, [loadData]);

  // Reset a la primera página si cambia el cliente activo
  useEffect(() => { setPage(1); }, [clienteActivoId]);

  const filtered = entregas.filter((e) => {
    if (!searchTerm) return true;
    const t = searchTerm.toLowerCase();
    return (
      e.consecutivo?.toLowerCase().includes(t) ||
      e.conductor?.nombre?.toLowerCase().includes(t) ||
      e.recibe?.nombre?.toLowerCase().includes(t) ||
      e.estado?.toLowerCase().includes(t)
    );
  });

  const formatDate = (d) => {
    if (!d) return '-';
    return new Date(d).toLocaleDateString('es-CO', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  const formatDateTime = (d) => {
    if (!d) return '-';
    return new Date(d).toLocaleString('es-CO', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  };

  const getEstadoBadge = (estado) => {
    const map = {
      'Entregado': 'bg-green-100 text-green-800',
      'En Camino': 'bg-yellow-100 text-yellow-800',
      'Alistado': 'bg-blue-100 text-blue-800',
      'Cancelado': 'bg-red-100 text-red-800',
    };
    return map[estado] || 'bg-gray-100 text-gray-800';
  };

  const totalPages = Math.ceil(totalRecords / ITEMS_PER_PAGE);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-white border-b shadow-sm">
        <div className="flex items-center gap-2 px-4 py-3">
          <Button variant="ghost" size="icon" onClick={() => navigate('/mis-prendas')} className="h-8 w-8 shrink-0">
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div className="flex items-center gap-2 min-w-0">
            <CheckCircle className="h-5 w-5 text-green-600 shrink-0" />
            <h1 className="text-base font-semibold text-gray-900 truncate">Entrega Prendas Limpias</h1>
          </div>
          <div className="ml-auto flex items-center gap-2 shrink-0">
            <ClienteActivoSelector
              clientes={clientesAsignados}
              activoId={clienteActivoId}
              onSelect={setClienteActivo}
              variant="inline"
            />
            <Badge className="bg-green-100 text-green-800">{totalRecords}</Badge>
          </div>
        </div>
        <div className="px-4 pb-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input
              placeholder="Buscar por consecutivo, conductor..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 h-9 text-sm"
            />
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="p-3 space-y-2">
        {sinClientes ? (
          <Card>
            <CardContent className="p-10 text-center">
              <Building2 className="h-14 w-14 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-700 font-medium">No tienes clientes asignados</p>
              <p className="text-sm text-gray-500 mt-1">Pide al administrador que te asigne uno o varios clientes.</p>
            </CardContent>
          </Card>
        ) : isLoading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-green-600 mx-auto" />
            <p className="text-sm text-gray-500 mt-3">Cargando...</p>
          </div>
        ) : filtered.length === 0 ? (
          <Card>
            <CardContent className="p-10 text-center">
              <CheckCircle className="h-14 w-14 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500 text-sm">No hay entregas registradas</p>
            </CardContent>
          </Card>
        ) : (
          filtered.map((e, idx) => {
            const totalConfirmadas = (e.items || []).reduce((s, i) => s + (i.cantidad_confirmada || 0), 0);
            const totalAlistadas = (e.items || []).reduce((s, i) => s + (i.cantidad_alistada || 0), 0);
            const hasDiff = e.estado === 'Entregado' && totalConfirmadas !== totalAlistadas;
            return (
              <motion.div key={e._id} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: idx * 0.03 }}>
                <Card
                  className="border-l-4 border-l-green-400 cursor-pointer hover:shadow-md active:scale-[0.99] transition-all"
                  onClick={() => setSelected(e)}
                >
                  <CardContent className="p-3">
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1 flex-wrap">
                          <span className="font-semibold text-sm text-green-700">{e.consecutivo}</span>
                          <Badge className={`text-xs ${getEstadoBadge(e.estado)}`}>{e.estado}</Badge>
                          {hasDiff && <Badge className="text-xs bg-orange-100 text-orange-800">Ajustado</Badge>}
                        </div>
                        <div className="flex flex-wrap gap-3 text-xs text-gray-500">
                          <span className="flex items-center gap-1">
                            <Package className="h-3 w-3" />
                            {e.estado === 'Entregado' ? totalConfirmadas : totalAlistadas} prendas
                          </span>
                          <span className="flex items-center gap-1">
                            <Calendar className="h-3 w-3" />
                            {formatDate(e.fecha_entrega || e.fecha_creacion)}
                          </span>
                          {e.conductor?.nombre && (
                            <span className="flex items-center gap-1">
                              <Truck className="h-3 w-3" />{e.conductor.nombre}
                            </span>
                          )}
                          {e.totalBolsas > 0 && (
                            <span className="flex items-center gap-1">
                              <Briefcase className="h-3 w-3" />{e.totalBolsas} bolsa(s)
                            </span>
                          )}
                        </div>
                      </div>
                      <ChevronRight className="h-4 w-4 text-gray-300 shrink-0 mt-1" />
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            );
          })
        )}

        {totalPages > 1 && (
          <div className="flex items-center justify-center gap-2 pt-2">
            <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>
              Anterior
            </Button>
            <span className="text-sm text-gray-500">{page} / {totalPages}</span>
            <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>
              Siguiente
            </Button>
          </div>
        )}
      </div>

      {/* Modal detalle */}
      <Dialog open={!!selected} onOpenChange={() => setSelected(null)}>
        <DialogContent className="!fixed !inset-0 !left-0 !top-0 !translate-x-0 !translate-y-0 !max-w-none !w-full !h-full !rounded-none !p-0 !gap-0 !border-0 flex flex-col [&>button]:hidden">
          <DialogTitle className="sr-only">Detalle entrega</DialogTitle>
          {selected && (
            <EntregaDetail
              e={selected}
              onClose={() => setSelected(null)}
              formatDateTime={formatDateTime}
              getEstadoBadge={getEstadoBadge}
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

const EntregaDetail = ({ e, onClose, formatDateTime, getEstadoBadge }) => {
  const totalAlistadas = (e.items || []).reduce((s, i) => s + (i.cantidad_alistada || 0), 0);
  const totalConfirmadas = (e.items || []).reduce((s, i) => s + (i.cantidad_confirmada || 0), 0);
  const hasDiff = e.estado === 'Entregado' && totalAlistadas !== totalConfirmadas;

  return (
    <div className="flex flex-col h-full">
      <div className="bg-green-600 text-white p-4 flex items-center justify-between shrink-0">
        <div>
          <h2 className="text-lg font-bold">{e.consecutivo}</h2>
          <p className="text-green-100 text-sm">Entrega prendas limpias</p>
        </div>
        <Button size="icon" variant="ghost" className="text-white hover:bg-green-700 h-8 w-8" onClick={onClose}>
          <ArrowLeft className="h-5 w-5" />
        </Button>
      </div>

      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {/* Info */}
        <div className="grid grid-cols-2 gap-3">
          <div className="bg-gray-50 rounded-lg p-3">
            <p className="text-xs text-gray-500 mb-1">Estado</p>
            <Badge className={`text-xs ${getEstadoBadge(e.estado)}`}>{e.estado}</Badge>
          </div>
          <div className="bg-gray-50 rounded-lg p-3">
            <p className="text-xs text-gray-500 mb-1">Tipo entrega</p>
            <p className="text-sm font-medium capitalize">{e.tipoEntrega || 'Ordinaria'}</p>
          </div>
          <div className="bg-gray-50 rounded-lg p-3">
            <p className="text-xs text-gray-500 mb-1">Conductor</p>
            <div className="flex items-center gap-1">
              <Truck className="h-3.5 w-3.5 text-gray-400" />
              <p className="text-sm font-medium">{e.conductor?.nombre || '-'}</p>
            </div>
          </div>
          <div className="bg-gray-50 rounded-lg p-3">
            <p className="text-xs text-gray-500 mb-1">Recibió</p>
            <div className="flex items-center gap-1">
              <User className="h-3.5 w-3.5 text-gray-400" />
              <p className="text-sm font-medium">{e.recibe?.nombre || '-'}</p>
            </div>
          </div>
          <div className="bg-gray-50 rounded-lg p-3">
            <p className="text-xs text-gray-500 mb-1">Fecha envío</p>
            <p className="text-sm font-medium">{formatDateTime(e.fecha_envio)}</p>
          </div>
          <div className="bg-gray-50 rounded-lg p-3">
            <p className="text-xs text-gray-500 mb-1">Fecha entrega</p>
            <p className="text-sm font-medium">{formatDateTime(e.fecha_entrega)}</p>
          </div>
        </div>

        {/* Resumen prendas */}
        <div className="flex items-center gap-4 bg-green-50 rounded-lg p-3">
          {e.estado === 'Entregado' ? (
            <>
              <div className="text-center">
                <p className="text-2xl font-bold text-green-700">{totalConfirmadas}</p>
                <p className="text-xs text-green-600">Confirmadas</p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-bold text-gray-400">{totalAlistadas}</p>
                <p className="text-xs text-gray-400">Alistadas</p>
              </div>
              {hasDiff && (
                <div className="text-center">
                  <p className={`text-2xl font-bold ${totalConfirmadas - totalAlistadas > 0 ? 'text-blue-600' : 'text-red-600'}`}>
                    {totalConfirmadas - totalAlistadas > 0 ? '+' : ''}{totalConfirmadas - totalAlistadas}
                  </p>
                  <p className="text-xs text-orange-600">Diferencia</p>
                </div>
              )}
            </>
          ) : (
            <div className="text-center">
              <p className="text-2xl font-bold text-green-700">{totalAlistadas}</p>
              <p className="text-xs text-green-600">Prendas</p>
            </div>
          )}
          {e.totalBolsas > 0 && (
            <div className="text-center">
              <p className="text-2xl font-bold text-blue-600">{e.totalBolsas}</p>
              <p className="text-xs text-blue-600">Bolsa(s)</p>
            </div>
          )}
        </div>

        {/* Items */}
        <div>
          <h3 className="font-semibold text-sm text-gray-700 mb-2">
            Detalle prendas ({(e.items || []).length} tipos)
          </h3>
          <div className="space-y-1">
            {(e.items || []).map((item, i) => {
              const diff = (item.cantidad_confirmada || 0) - (item.cantidad_alistada || 0);
              const showConfirmada = e.estado === 'Entregado';
              return (
                <div key={i} className="flex items-center justify-between text-sm p-2.5 bg-gray-50 rounded-lg">
                  <span className="truncate flex-1 font-medium">{item.nombre}</span>
                  <div className="flex items-center gap-2 shrink-0 ml-2">
                    {showConfirmada && (
                      <span className="text-gray-400 text-xs">Alistada: {item.cantidad_alistada}</span>
                    )}
                    <span className={`font-semibold ${showConfirmada && diff !== 0 ? 'text-orange-600' : 'text-green-600'}`}>
                      {showConfirmada ? (item.cantidad_confirmada || 0) : item.cantidad_alistada}
                    </span>
                    {showConfirmada && diff !== 0 && (
                      <Badge variant="outline" className={`text-xs ${diff > 0 ? 'text-blue-600 border-blue-300' : 'text-red-600 border-red-300'}`}>
                        {diff > 0 ? '+' : ''}{diff}
                      </Badge>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {e.observaciones && (
          <div className="bg-yellow-50 rounded-lg p-3">
            <p className="text-xs text-yellow-700 font-semibold mb-1">Observaciones</p>
            <p className="text-sm text-yellow-800">{e.observaciones}</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ClientePrendasEntregas;
